// ================================================================
// BOOKINGS LIST COMPONENT
// ================================================================

import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Layout from '../components/Layout';
import '../components/booking-calendar.css';
import '../components/booking-components.css';

// Helper: format date as YYYY-MM-DD in LOCAL timezone
const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const BookingsList = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [agentFilter, setAgentFilter] = useState('');
  const [agentsList, setAgentsList] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canManageOthers = user.manage_others_bookings === true;

  useEffect(() => {
    fetchBookings();
    if (canManageOthers) fetchAgents();
    const interval = setInterval(fetchBookings, 30000);
    return () => clearInterval(interval);
  }, [agentFilter]);

  const fetchAgents = async () => {
    try {
      const response = await api.get('/bookings/available-agents');
      const data = response.data;
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (data && Array.isArray(data.data)) list = data.data;
      else if (data && Array.isArray(data.agents)) list = data.agents;
      setAgentsList(list);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = {};
      if (agentFilter) params.agent_id = agentFilter;
      const response = await api.get('/bookings', { params });
      const data = response.data;
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (data && Array.isArray(data.data)) list = data.data;
      console.log(`✅ Fetched ${list.length} bookings`);
      setBookings(list);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    try {
      await api.put(`/bookings/${bookingId}/cancel`, {
        reason: 'Cancelled by staff'
      });
      fetchBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'confirmed': return 'badge-success';
      case 'pending': return 'badge-warning';
      case 'cancelled': return 'badge-danger';
      case 'completed': return 'badge-info';
      default: return 'badge-info';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Handle YYYY-MM-DD format - add T12:00 to avoid UTC shift
    const d = new Date(dateStr + (dateStr.length === 10 ? 'T12:00:00' : ''));
    return d.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const formatMeetingType = (slug) => {
    if (!slug) return '';
    return slug.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Filter bookings based on selected tab
  const filteredBookings = bookings.filter((booking) => {
    const todayStr = toLocalDateStr(new Date());
    const bookingDate = booking.booking_date
      ? (typeof booking.booking_date === 'string'
        ? booking.booking_date.split('T')[0]
        : toLocalDateStr(new Date(booking.booking_date)))
      : '';

    switch (filter) {
      case 'today':
        return bookingDate === todayStr;
      case 'upcoming':
        return bookingDate >= todayStr && booking.status !== 'cancelled';
      case 'past':
        return bookingDate < todayStr || booking.status === 'cancelled';
      default:
        return true;
    }
  });

  // Stats
  const todayStr = toLocalDateStr(new Date());
  const pendingCount = bookings.filter(b => b.status === 'pending').length;
  const confirmedCount = bookings.filter(b => b.status === 'confirmed').length;
  const todayCount = bookings.filter(b => {
    const bd = b.booking_date ? (typeof b.booking_date === 'string' ? b.booking_date.split('T')[0] : toLocalDateStr(new Date(b.booking_date))) : '';
    return bd === todayStr && b.status !== 'cancelled';
  }).length;

  const openEditModal = (booking) => {
    const bDate = booking.booking_date ? (typeof booking.booking_date === 'string' ? booking.booking_date.split('T')[0] : toLocalDateStr(new Date(booking.booking_date))) : '';
    setEditBooking(booking);
setEditForm({
      booking_date: bDate,
      start_time: booking.start_time ? booking.start_time.substring(0, 5) : '',
      duration: booking.duration || 30,
      connection_method: booking.connection_method || '',
      agent_id: booking.agent_id || '',
      update_reason: '',
      participants: (booking.participants || []).filter(p => p.role === 'participant')
    });
    setShowEditModal(true);
  };

  const calcEndTime = (startTime, duration) => {
    if (!startTime) return '';
    const [h, m] = startTime.split(':').map(Number);
    const totalMin = h * 60 + m + (parseInt(duration) || 30);
    const eh = Math.floor(totalMin / 60) % 24;
    const em = totalMin % 60;
    return String(eh).padStart(2, '0') + ':' + String(em).padStart(2, '0');
  };

  const handleEditSubmit = async () => {
    setSaving(true);
    try {
      const endTime = calcEndTime(editForm.start_time, editForm.duration);
     const payload = {
        booking_date: editForm.booking_date,
        start_time: editForm.start_time + ':00',
        end_time: endTime + ':00',
        duration: parseInt(editForm.duration),
        connection_method: editForm.connection_method,
        agent_id: editForm.agent_id,
        update_reason: editForm.update_reason,
        participant_ids: (editForm.participants || []).map(p => p.agent_id || p.id)
      };
      await api.put('/bookings/' + editBooking.id + '/update', payload);
      alert('Booking updated successfully! Customer will be notified.');
      setShowEditModal(false);
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Failed to update booking');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="bookings-list-container">
        {/* Header */}
        <div className="page-header mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">📋 My Bookings</h1>
              <p className="page-subtitle">Manage your scheduled appointments</p>
            </div>
            <button className="btn btn-primary" onClick={() => window.location.href = '/booking-calendar'}>
              <span className="nav-icon">➕</span> New Booking
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-6 p-4">
          <div className="flex gap-3">
            <button className={`btn ${filter === 'upcoming' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setFilter('upcoming')}>
              <span className="nav-icon">📅</span> Upcoming
            </button>
            <button className={`btn ${filter === 'today' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setFilter('today')}>
              <span className="nav-icon">🔔</span> Today
            </button>
            <button className={`btn ${filter === 'past' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setFilter('past')}>
              <span className="nav-icon">📜</span> Past
            </button>
            <button className="btn btn-ghost ml-auto" onClick={fetchBookings}>
              <span className="nav-icon">🔄</span> Refresh
            </button>
          </div>
          {canManageOthers && (
            <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '14px', fontWeight: '500' }}>👤 الموظف:</span>
              <select
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color, #333)',
                  background: 'var(--card-bg, #1a1a2e)',
                  color: 'var(--text-primary, #fff)',
                  fontSize: '14px',
                  minWidth: '200px'
                }}
              >
                <option value="">📋 الكل</option>
                {agentsList.map(agent => (
                  <option key={agent.id} value={agent.id}>{agent.full_name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="stats-grid mb-6">
          <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-details">
              <div className="stat-value">{pendingCount}</div>
              <div className="stat-label">PENDING</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-details">
              <div className="stat-value">{confirmedCount}</div>
              <div className="stat-label">CONFIRMED</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🔔</div>
            <div className="stat-details">
              <div className="stat-value">{todayCount}</div>
              <div className="stat-label">TODAY</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-details">
              <div className="stat-value">{bookings.length}</div>
              <div className="stat-label">TOTAL</div>
            </div>
          </div>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="card p-8">
            <div className="loading-spinner">Loading bookings...</div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="card p-8">
            <div className="empty-state">
              <div className="empty-state-icon">📭</div>
              <div className="empty-state-text">No bookings found</div>
            </div>
          </div>
        ) : (
          <div className="card p-6">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date & Time</th>
                    <th>Customer</th>
                    <th>Phone</th>
                    <th>Agent</th>
                    <th>Type</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => (
                    <tr key={booking.id}>
                      <td>
                        <div className="booking-time">
                          <span className="nav-icon">📅</span>
                          <div>
                            <div>{formatDate(booking.booking_date)}</div>
                            <div className="text-secondary">
                              {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="booking-customer">
                          <div>{booking.customer_name}</div>
                          {booking.customer_email && (
                            <div className="text-tertiary">{booking.customer_email}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="text-secondary">{booking.customer_phone}</div>
                      </td>
                     <td>
                        <div className="text-secondary">
                          {booking.agent_name || '-'}
                          {booking.participants && booking.participants.length > 0 && (
                            <div style={{ fontSize: '11px', color: '#a78bfa', marginTop: '2px' }}>
                              👥 +{booking.participants.filter(p => p.role === 'participant').length} {booking.participants.filter(p => p.role === 'participant').map(p => p.agent_name || p.full_name).join(', ')}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="booking-type">{formatMeetingType(booking.meeting_type)}</div>
                      </td>
                      <td>
                        <div className="text-secondary">
                          {(() => {
                            if (booking.start_time && booking.end_time) {
                              const [sh, sm] = booking.start_time.split(':').map(Number);
                              const [eh, em] = booking.end_time.split(':').map(Number);
                              return ((eh * 60 + em) - (sh * 60 + sm)) + ' min';
                            }
                            return (booking.duration || '-') + ' min';
                          })()}
                        </div>                      </td>
                      <td>
                        <span className={`badge-status ${getStatusBadgeClass(booking.status)}`}>
                          {booking.status}
                        </span>
                      </td>
                      <td>
                        <div className="booking-actions flex gap-2">
                          {(booking.status === 'pending' || booking.status === 'confirmed') && (
                            <button className="btn btn-sm btn-outline"
                              onClick={() => handleCancel(booking.id)}>
                              Cancel
                            </button>
                          )}
                          {(booking.status === 'pending' || booking.status === 'confirmed') && (
                            <button className="btn btn-sm btn-primary"
                              onClick={() => openEditModal(booking)}>
                              Edit
                            </button>
                          )}
                          <button className="btn btn-sm btn-ghost"
                            onClick={() => { setSelectedBooking(booking); setShowModal(true); }}>
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Booking Details Modal */}
        {showModal && selectedBooking && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="text-primary">Booking Details</h3>
                <button className="btn btn-ghost" onClick={() => setShowModal(false)}>✕</button>
              </div>

              <div className="modal-body">
                <div className="booking-summary-card">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">Customer Name</label>
                      <div className="text-primary">{selectedBooking.customer_name}</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone Number</label>
                      <div className="text-primary">{selectedBooking.customer_phone}</div>
                    </div>
                    {selectedBooking.customer_email && (
                      <div className="form-group">
                        <label className="form-label">Email</label>
                        <div className="text-primary">{selectedBooking.customer_email}</div>
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">Agent</label>
                      <div className="text-primary">{selectedBooking.agent_name || '-'}</div>
                    </div>
                    {selectedBooking.participants && selectedBooking.participants.filter(p => p.role === 'participant').length > 0 && (
                      <div className="form-group">
                        <label className="form-label">👥 Participants</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {selectedBooking.participants.filter(p => p.role === 'participant').map(p => (
                            <span key={p.agent_id} style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px',
                              padding: '4px 10px', borderRadius: '16px',
                              background: 'rgba(139, 92, 246, 0.15)',
                              border: '1px solid rgba(139, 92, 246, 0.3)',
                              fontSize: '13px', color: '#a78bfa'
                            }}>
                              <span style={{
                                width: '20px', height: '20px', borderRadius: '50%',
                                background: '#8b5cf6', display: 'inline-flex',
                                alignItems: 'center', justifyContent: 'center',
                                fontSize: '10px', color: 'white'
                              }}>{(p.agent_name || p.full_name || '?').charAt(0).toUpperCase()}</span>
                              {p.agent_name || p.full_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">Meeting Type</label>
                      <div className="text-primary">{formatMeetingType(selectedBooking.meeting_type)}</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Date</label>
                      <div className="text-primary">{formatDate(selectedBooking.booking_date)}</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Time</label>
                      <div className="text-primary">
                        {formatTime(selectedBooking.start_time)} - {formatTime(selectedBooking.end_time)}
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Duration</label>
                      <div className="text-primary">{selectedBooking.duration || '-'} minutes</div>
                    </div>
                    {selectedBooking.connection_method && (
                      <div className="form-group">
                        <label className="form-label">Meeting Method</label>
                        <div className="text-primary">{selectedBooking.connection_method.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</div>
                      </div>
                    )}
                    {selectedBooking.google_meet_link && (
                      <div className="form-group">
                        <label className="form-label">Meeting Link</label>
                        <a href={selectedBooking.google_meet_link}
                          target="_blank" rel="noopener noreferrer"
                          style={{ color: '#667eea', textDecoration: 'underline', wordBreak: 'break-all' }}>
                          📹 {selectedBooking.google_meet_link}
                        </a>
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <select
                          value={selectedBooking.status}
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            try {
                              await api.patch(`/bookings/${selectedBooking.id}/status`, { status: newStatus });
                              setSelectedBooking({ ...selectedBooking, status: newStatus });
                              fetchBookings();
                            } catch (err) {
                              console.error('Error updating status:', err);
                              alert('Failed to update status');
                            }
                          }}
                          style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border-color, #333)', background: 'var(--card-bg, #1a1a2e)', color: 'var(--text-primary, #fff)' }}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="completed">Completed</option>
                          <option value="no_show">No Show</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                    </div>
                    
                  </div>

                  {selectedBooking.meeting_reason && (
                    <div className="form-group mt-4">
                      <label className="form-label">Meeting Reason</label>
                      <div className="text-secondary">{selectedBooking.meeting_reason}</div>
                    </div>
                  )}

                  {selectedBooking.google_meet_link && (
                    <div className="form-group mt-4">
                      <label className="form-label">Meeting Link</label>
                      <a href={selectedBooking.google_meet_link}
                        target="_blank" rel="noopener noreferrer"
                        className="btn btn-primary btn-sm">
                        📹 Join Meeting
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                {(selectedBooking.status === 'pending' || selectedBooking.status === 'confirmed') && (
                  <button className="btn btn-outline"
                    onClick={() => { handleCancel(selectedBooking.id); setShowModal(false); }}>
                    Cancel Booking
                  </button>
                )}
                <button className="btn btn-primary" onClick={() => setShowModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Booking Modal */}
      {showEditModal && editBooking && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3 className="text-primary">Edit Booking #{editBooking.id}</h3>
              <button className="btn btn-ghost" onClick={() => setShowEditModal(false)}>x</button>
            </div>

            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                <div className="form-group">
                  <label className="form-label">Customer</label>
                  <div className="text-primary" style={{ fontWeight: '600' }}>{editBooking.customer_name} ({editBooking.customer_phone})</div>
                </div>

                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input type="date" className="form-control" value={editForm.booking_date}
                    onChange={(e) => setEditForm({...editForm, booking_date: e.target.value})}
                    style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color, #333)', background: 'var(--card-bg, #1a1a2e)', color: 'var(--text-primary, #fff)', width: '100%' }} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Start Time</label>
                    <input type="time" className="form-control" value={editForm.start_time}
                      onChange={(e) => setEditForm({...editForm, start_time: e.target.value})}
                      style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color, #333)', background: 'var(--card-bg, #1a1a2e)', color: 'var(--text-primary, #fff)', width: '100%' }} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Duration (min)</label>
                    <select value={editForm.duration}
                      onChange={(e) => setEditForm({...editForm, duration: e.target.value})}
                      style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color, #333)', background: 'var(--card-bg, #1a1a2e)', color: 'var(--text-primary, #fff)', width: '100%' }}>
                      <option value="15">15 min</option>
                      <option value="30">30 min</option>
                      <option value="45">45 min</option>
                      <option value="60">60 min</option>
                      <option value="90">90 min</option>
                      <option value="120">120 min</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Meeting Method</label>
                  <select value={editForm.connection_method}
                    onChange={(e) => setEditForm({...editForm, connection_method: e.target.value})}
                    style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color, #333)', background: 'var(--card-bg, #1a1a2e)', color: 'var(--text-primary, #fff)', width: '100%' }}>
                    <option value="">-- Select --</option>
                    <option value="google_meet">Google Meet</option>
                    <option value="zoom">Zoom</option>
                    <option value="phone">Phone Call</option>
                    <option value="in_person">In Person</option>
                  </select>
                </div>

                {canManageOthers && (
                  <div className="form-group">
                    <label className="form-label">Agent</label>
                    <select value={editForm.agent_id}
                      onChange={(e) => setEditForm({...editForm, agent_id: e.target.value})}
                      style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color, #333)', background: 'var(--card-bg, #1a1a2e)', color: 'var(--text-primary, #fff)', width: '100%' }}>
                      {agentsList.map(a => (
                        <option key={a.id} value={a.id}>{a.full_name}</option>
                      ))}
                    </select>
                  </div>
                )}

               <div className="form-group">
                  <label className="form-label">👥 Participants</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                    {(editForm.participants || []).map(p => (
                      <span key={p.agent_id || p.id} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '4px 10px', borderRadius: '16px',
                        background: 'rgba(139, 92, 246, 0.15)',
                        border: '1px solid rgba(139, 92, 246, 0.3)',
                        fontSize: '13px', color: '#a78bfa'
                      }}>
                        {p.agent_name || p.full_name}
                        <button type="button" onClick={() => {
                          setEditForm({...editForm, participants: editForm.participants.filter(x => (x.agent_id || x.id) !== (p.agent_id || p.id))});
                        }} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '14px', padding: '0 2px' }}>×</button>
                      </span>
                    ))}
                  </div>
                  <select
                    onChange={(e) => {
                      const agentId = parseInt(e.target.value);
                      if (!agentId) return;
                      const agent = agentsList.find(a => a.id === agentId);
                      if (!agent) return;
                      const exists = (editForm.participants || []).find(p => (p.agent_id || p.id) === agentId);
                      if (exists) return;
                      if (agentId === parseInt(editForm.agent_id)) return;
                      setEditForm({...editForm, participants: [...(editForm.participants || []), { agent_id: agentId, full_name: agent.full_name, agent_name: agent.full_name, role: 'participant' }]});
                      e.target.value = '';
                    }}
                    style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color, #333)', background: 'var(--card-bg, #1a1a2e)', color: 'var(--text-primary, #fff)', width: '100%' }}>
                    <option value="">+ Add participant...</option>
                    {agentsList.filter(a => a.id !== parseInt(editForm.agent_id) && !(editForm.participants || []).find(p => (p.agent_id || p.id) === a.id)).map(a => (
                      <option key={a.id} value={a.id}>{a.full_name}</option>
                    ))}
                  </select>
               </div>

                <div className="form-group">
                  <label className="form-label">Reason for change (optional)</label>
                  <input type="text" className="form-control" placeholder="e.g. Customer requested reschedule"
                    value={editForm.update_reason}
                    onChange={(e) => setEditForm({...editForm, update_reason: e.target.value})}
                    style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color, #333)', background: 'var(--card-bg, #1a1a2e)', color: 'var(--text-primary, #fff)', width: '100%' }} />
                </div>

              </div>
            </div>

            <div className="modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEditSubmit} disabled={saving}>
                {saving ? 'Saving...' : 'Save & Notify Customer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default BookingsList;